
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ProjectForm from "@/components/ProjectForm";
import { Project } from "@/types/project";
import { Loader2, X, UserX } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SharePermission } from "@/types/share";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProjectDialogsProps {
  selectedProjectForSharing: Project | null;
  showProjectForm: boolean;
  editingProject: Project | null;
  duplicatingProject: Project | null;
  onCloseShare: () => void;
  onCloseForm: () => void;
  onSubmit: (project: Project) => Promise<void>;
}

export function ProjectDialogs({
  selectedProjectForSharing,
  showProjectForm,
  editingProject,
  duplicatingProject,
  onCloseShare,
  onCloseForm,
  onSubmit,
}: ProjectDialogsProps) {
  const isMobile = useIsMobile();
  const isFormOpen = showProjectForm || !!editingProject || !!duplicatingProject;
  const [email, setEmail] = useState("");
  const [isSharing, setIsSharing] = useState(false);
  const [permission, setPermission] = useState<SharePermission>("view");
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const { user } = useAuth();
  const [sharesList, setSharesList] = useState<any[]>([]);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const fetchShares = async () => {
    if (!selectedProjectForSharing?.id) return;
    
    try {
      const { data, error } = await supabase
        .from("project_shares")
        .select(`
          *,
          shared_with_profile:profiles!project_shares_shared_with_fkey(
            full_name,
            email
          )
        `)
        .eq("project_id", selectedProjectForSharing.id);

      if (error) throw error;
      setSharesList(data || []);
    } catch (error) {
      console.error('Error fetching shares:', error);
    }
  };

  // Fetch shares when the dialog opens
  useState(() => {
    if (selectedProjectForSharing) {
      fetchShares();
    }
  });

  const handleShare = async () => {
    if (!email || !selectedProjectForSharing || !user) {
      toast.error("Invalid email or project");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setIsSharing(true);

    try {
      // Check if the user exists
      const { data: profiles, error: profileError } = await supabase
        .from('profiles')
        .select('id, email')
        .ilike('email', email.trim());

      if (profileError) {
        console.error('Error finding user:', profileError);
        toast.error("Failed to find user profile");
        return;
      }

      if (!profiles || profiles.length === 0) {
        setShowInviteDialog(true);
        setIsSharing(false);
        return;
      }

      const targetProfile = profiles[0];

      // Check if the project is already shared with this user
      const { data: existingShares, error: sharesError } = await supabase
        .from('project_shares')
        .select('id')
        .eq('project_id', selectedProjectForSharing.id)
        .eq('shared_with', targetProfile.id)
        .limit(1);

      if (sharesError) {
        console.error('Error checking existing shares:', sharesError);
        toast.error("Failed to check existing shares");
        setIsSharing(false);
        return;
      }

      if (existingShares && existingShares.length > 0) {
        toast.error("Project already shared with this user");
        setIsSharing(false);
        return;
      }

      // Share the project
      const { error: shareError } = await supabase
        .from("project_shares")
        .insert({
          project_id: selectedProjectForSharing.id,
          shared_by: user.id,
          shared_with: targetProfile.id,
          permission,
        });

      if (shareError) {
        console.error('Error sharing project:', shareError);
        toast.error("Failed to share project");
        setIsSharing(false);
        return;
      }

      toast.success("Project shared successfully");
      setEmail("");
      fetchShares();
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error("An error occurred while sharing the project");
    } finally {
      setIsSharing(false);
    }
  };

  const handleUpdatePermission = async (shareId: string, newPermission: SharePermission) => {
    try {
      const { error } = await supabase
        .from("project_shares")
        .update({ permission: newPermission })
        .eq("id", shareId);

      if (error) {
        toast.error("Failed to update permission");
        return;
      }

      toast.success("Permission updated successfully");
      fetchShares();
    } catch (error) {
      console.error('Error updating permission:', error);
      toast.error("An error occurred while updating the permission");
    }
  };

  const handleRemoveShare = async (shareId: string) => {
    try {
      const { error } = await supabase
        .from("project_shares")
        .delete()
        .eq("id", shareId);

      if (error) {
        toast.error("Failed to remove share");
        return;
      }

      toast.success("Share removed successfully");
      fetchShares();
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error("An error occurred while removing the share");
    }
  };

  const handleInviteUser = async () => {
    if (!email || !selectedProjectForSharing || !user) return;
    
    setIsSharing(true);
    try {
      const { data: invitationLink, error: inviteError } = await supabase
        .from('invitation_links')
        .insert({
          created_by: user.id,
          max_uses: 1,
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          metadata: {
            projectId: selectedProjectForSharing.id,
            permission,
            inviterEmail: user.email
          }
        })
        .select()
        .single();

      if (inviteError) throw inviteError;

      const { error: functionError } = await supabase.functions.invoke('send-invitation', {
        body: {
          email,
          invitationId: invitationLink.id,
          projectId: selectedProjectForSharing.id,
          inviterName: user.email,
          permission
        }
      });

      if (functionError) throw functionError;

      toast.success("Invitation sent successfully");
      onCloseShare();
      setEmail("");
      setShowInviteDialog(false);
    } catch (error) {
      console.error('Error sending invitation:', error);
      toast.error("Failed to send invitation");
    } finally {
      setIsSharing(false);
    }
  };

  // Mobile Share Dialog UI
  const MobileShareDialog = () => (
    <Sheet 
      open={!!selectedProjectForSharing} 
      onOpenChange={(open) => {
        if (!open) onCloseShare();
      }}
    >
      <SheetContent side="bottom" className="h-[90vh] p-0 pt-6 max-h-[90vh] overflow-hidden bg-background rounded-t-3xl">
        <div className="flex flex-col h-full relative px-6">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-2xl font-bold">Share Project</h2>
            <Button variant="ghost" size="icon" onClick={onCloseShare} className="absolute right-4 top-0">
              <X className="h-5 w-5" />
            </Button>
          </div>
          
          <div className="text-muted-foreground mb-6">
            Share your project with team members.
          </div>
          
          {sharesList.length > 0 && (
            <div className="space-y-2 mb-6">
              <h3 className="text-lg font-medium mb-2">Currently shared with</h3>
              <div className="space-y-3 max-h-[30vh] overflow-y-auto pb-2">
                {sharesList.map((share) => (
                  <div key={share.id} className="bg-secondary/80 rounded-lg p-3">
                    <div className="flex items-center justify-between">
                      <div className="truncate max-w-[60%]">
                        <div className="font-medium truncate">
                          {share.shared_with_profile?.full_name || share.shared_with_profile?.email || "Unnamed User"}
                        </div>
                        <div className="text-sm text-muted-foreground truncate">
                          {share.shared_with_profile?.email}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Select
                          defaultValue={share.permission}
                          onValueChange={(value: SharePermission) => handleUpdatePermission(share.id, value)}
                        >
                          <SelectTrigger className="w-[80px] h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="view">View</SelectItem>
                            <SelectItem value="edit">Edit</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8"
                          onClick={() => handleRemoveShare(share.id)}
                        >
                          <UserX className="h-4 w-4 text-muted-foreground" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          
          <div className="space-y-4 grow">
            <div>
              <Label className="text-base" htmlFor="email">Or enter email</Label>
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 bg-secondary/50 border-0"
              />
            </div>
            
            <div>
              <Label className="text-base">Permissions</Label>
              <RadioGroup value={permission} onValueChange={(value) => setPermission(value as SharePermission)} className="mt-2 space-y-2">
                <div className="flex items-center space-x-2 bg-secondary/50 p-3 rounded-lg">
                  <RadioGroupItem value="view" id="m-view" className="text-primary" />
                  <Label htmlFor="m-view" className="cursor-pointer font-medium">View only</Label>
                </div>
                <div className="flex items-center space-x-2 bg-secondary/50 p-3 rounded-lg">
                  <RadioGroupItem value="edit" id="m-edit" className="text-primary" />
                  <Label htmlFor="m-edit" className="cursor-pointer font-medium">Can edit</Label>
                </div>
              </RadioGroup>
            </div>
          </div>
          
          <div className="flex justify-end space-x-2 py-4 mt-auto sticky bottom-0 bg-background">
            <Button 
              variant="outline" 
              onClick={onCloseShare}
              className="px-6"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleShare} 
              disabled={isSharing || !email}
              className="px-6"
            >
              {isSharing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Share
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );

  // Desktop Share Dialog UI  
  const DesktopShareDialog = () => (
    <Dialog 
      open={!!selectedProjectForSharing} 
      onOpenChange={(open) => {
        if (!open) onCloseShare();
      }}
    >
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Share Project</DialogTitle>
        </DialogHeader>
        {selectedProjectForSharing && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground mb-4">
              Share this project with team members to collaborate.
            </p>
            
            {sharesList.length > 0 && (
              <div className="space-y-2 mb-4">
                <Label>Currently shared with</Label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {sharesList.map((share) => (
                    <Card key={share.id} className="bg-secondary p-3">
                      <div className="flex items-center justify-between">
                        <div className="truncate max-w-[60%]">
                          <div className="font-medium truncate">
                            {share.shared_with_profile?.full_name || share.shared_with_profile?.email || "Unnamed User"}
                          </div>
                          <div className="text-sm text-muted-foreground truncate">
                            {share.shared_with_profile?.email}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Select
                            defaultValue={share.permission}
                            onValueChange={(value: SharePermission) => handleUpdatePermission(share.id, value)}
                          >
                            <SelectTrigger className="w-[80px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="view">View</SelectItem>
                              <SelectItem value="edit">Edit</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleRemoveShare(share.id)}
                          >
                            <UserX className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="colleague@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Permission level</Label>
              <RadioGroup value={permission} onValueChange={(value) => setPermission(value as SharePermission)} className="space-y-2">
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="view" id="view" />
                  <Label htmlFor="view" className="cursor-pointer">View only</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="edit" id="edit" />
                  <Label htmlFor="edit" className="cursor-pointer">Can edit</Label>
                </div>
              </RadioGroup>
            </div>
            
            <div className="flex justify-end pt-4">
              <Button 
                onClick={handleShare} 
                disabled={isSharing}
              >
                {isSharing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Share
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <>
      {isMobile ? <MobileShareDialog /> : <DesktopShareDialog />}

      <AlertDialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>User not found</AlertDialogTitle>
            <AlertDialogDescription>
              This email address isn't registered. Would you like to send them an invitation to join and view this project?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowInviteDialog(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleInviteUser} className="bg-primary">
              {isSharing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Send Invitation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isMobile ? (
        // Mobile: Use a sheet that slides up from the bottom
        <Sheet open={isFormOpen} onOpenChange={(open) => !open && onCloseForm()}>
          <SheetContent side="bottom" className="h-[90vh] p-0 pt-6 max-h-[90vh] overflow-hidden">
            {isFormOpen && (
              <div className="h-full">
                <ProjectForm
                  onClose={onCloseForm}
                  onSubmit={onSubmit}
                  initialData={editingProject || duplicatingProject || undefined}
                />
              </div>
            )}
          </SheetContent>
        </Sheet>
      ) : (
        // Desktop: Use the centered modal
        isFormOpen && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50 overflow-y-auto">
            <div className="w-full max-w-lg animate-fade-in">
              <ProjectForm
                onClose={onCloseForm}
                onSubmit={onSubmit}
                initialData={editingProject || duplicatingProject || undefined}
              />
            </div>
          </div>
        )
      )}
    </>
  );
}
